import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import "./AgencyUsers.css";
import DeleteModal from "../../components/modals/DeleteModal";
import { Ripple } from "primereact/ripple";
import AddAgencyModal from "../../components/modals/AddUserModal";
import EditAgencyModal from "../../components/modals/editModals/EditUserModal";
import { deleteUser, getAgencyUsers } from "../../services/user.service";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import useToast from "../../context/toast.hook";
import CommonBackdrop from "../../components/loader";

const AgencyUsers = () => {
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [sortMeta, setSortMeta] = useState({ sortField: "", sortOrder: "" });
  const { errorToast, successToast, toastRef } = useToast();
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteProductDialog, setDeleteProductDialog] = useState(false);
  const [toggleAddUserModal, setToggleAddUserModal] = useState(false);
  const [toggleEditUserModal, setToggleEditUserModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    "country.name": {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    "representative.name": {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    company: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  });

  const template2 = {
    layout: "CurrentPageReport PrevPageLink PageLinks NextPageLink",
    CurrentPageReport: (options) => {
      return (
        <div className="mobileHideShowing">
          <span
            style={{
              color: "var(--text-color)",
              userSelect: "none",
              width: "160px",
              textAlign: "center",
            }}
          >
            Showing {options.first} - {options.last} of {options.totalRecords}
          </span>
        </div>
      );
    },
    PrevPageLink: (options) => {
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
          disabled={options.disabled}
        >
          <span className="p-3">{"<"}</span>
          <Ripple />
        </button>
      );
    },
    PageLinks: (options) => {
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
        >
          {options.page + 1}
          <Ripple />
        </button>
      );
    },
    NextPageLink: (options) => {
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
          disabled={options.disabled}
        >
          <span className="p-3">{">"}</span>
          <Ripple />
        </button>
      );
    },
  };

  const handleGetAgencyData = (currentPage, sorting) => {
    let filters = sorting
      ? {
          sortField: sorting.sortField,
          sortOrder: sorting.sortOrder,
        }
      : {
          sortField: sortMeta.sortField,
          sortOrder: sortMeta.sortOrder,
        };
    getAgencyUsers({ page: currentPage || page, limit: 10, filters })
      .then((res) => {
        setProducts(res.data?.data?.data);
        setTotalCount(res.data?.data?.totalCount);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        errorToast(error);
      });
  };

  useEffect(() => {
    handleGetAgencyData();
    // eslint-disable-next-line
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <Toast ref={toastRef} />
        <div className="headerButtons">
          <div className="startButtons">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                className="searchBox"
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Search"
              />
            </span>
          </div>
        </div>
      </>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          className="p-button-outlined p-button-icon-only mr-2"
          width="2rem"
          onClick={() => {
            setSelectedAgency(rowData);
            setToggleEditUserModal(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-danger p-button-outlined p-button-icon-only"
          onClick={() => {
            setSelectedAgency(rowData);

            setDeleteProductDialog(true);
          }}
        />
      </React.Fragment>
    );
  };

  const header = renderHeader();

  const handleConfirmDelete = () => {
    deleteUser(selectedAgency?._id)
      .then((res) => {
        successToast(res.data?.message);
        setPage(1);
        setDeleteProductDialog(false);
        handleGetAgencyData(1);
      })
      .catch((err) => {
        errorToast(err);
      });
  };

  const handleAgencyAdded = (onlyModalClose) => {
    if (onlyModalClose) {
      setToggleAddUserModal(false);
      return;
    }
    successToast("Agency added successfully");
    setToggleAddUserModal(false);
    setPage(1);
    handleGetAgencyData(1);
  };

  const handleAgencyUpdated = (onlyModalClose) => {
    if (onlyModalClose) {
      setToggleEditUserModal(false);
      return;
    }
    successToast("Agency updated successfully");
    setToggleEditUserModal(false);
    handleGetAgencyData();
  };

  const onPageChange = (event) => {
    setPage(event.page + 1);
    setFirst(event.first);
    handleGetAgencyData(event.page + 1);
  };

  const handleSort = async (e) => {
    setSortMeta({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    setPage(1);
    setFirst(0);
    handleGetAgencyData(1, {
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
  };

  return (
    <>
      <CommonBackdrop showBackdrop={showBackdrop} />
      {toggleAddUserModal && <AddAgencyModal toggleModal={handleAgencyAdded} />}
      {toggleEditUserModal && (
        <EditAgencyModal
          agencyData={selectedAgency}
          toggleModal={handleAgencyUpdated}
        />
      )}

      <div className="initialBox">
        <div className="welcomeBox">
          <strong>All Users</strong>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            style={{
              backgroundColor: "#1C9894",
              color: "white",
              border: "0",
              width: "172px",
              height: "41px",
            }}
            placeholder="Add Agency"
            label="Add New User"
            onClick={() => {
              setToggleAddUserModal(true);
            }}
          />
        </div>
      </div>

      {!loading && (
        <DataTable
          value={products}
          stripedRows
          responsiveLayout="scroll"
          selectionMode="single"
          dataKey="id"
          header={header}
          filters={filters}
          emptyMessage="No Record found."
          sortField={sortMeta.sortField}
          sortOrder={sortMeta.sortOrder}
          sortMode="single"
          onSort={handleSort}
        >
          <Column
            field="name"
            header="Name"
            sortable
            style={{ backgroundColor: "" }}
          />
          <Column
            field="agencyUserName"
            header="Username"
            sortable
            style={{ backgroundColor: "" }}
          />

          <Column field="email" header="Email" sortable />
          <Column field="phone" header="Phone" sortable />
          <Column field="contact" header="Contact" sortable />
          <Column header="Actions" body={actionBodyTemplate} />
        </DataTable>
      )}
      <br />
      <br />

      <Paginator
        first={first}
        template={template2}
        rows={10}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />

      {loading && <Skeleton height="28rem" />}

      {deleteProductDialog && (
        <DeleteModal
          deleteProductDialog={deleteProductDialog}
          onHide={() => setDeleteProductDialog(false)}
          onConfirm={handleConfirmDelete}
          customMessage="Associated data will also be deleted, are you sure you want to delete this agency?"
        />
      )}
    </>
  );
};

export default AgencyUsers;
